import { useState } from 'react';
import { Link } from 'react-router';
import { Avatar, DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatXaf, rentPeriodNoun } from '../../lib/format.js';
import { whatsappLink } from '../../lib/whatsapp.js';

/**
 * Shown once, after an account is created or a password is reset. The password is
 * hashed the moment it is generated, so this really is the only chance to read it:
 * there is no screen anywhere that can show it again.
 */
const PasswordHandover = ({ tenant, password, onClose }) => {
  const toast = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Copied.');
    } catch {
      toast.info('Copy it by hand: your browser blocked the clipboard.');
    }
  };

  return (
    <Modal
      title="Give this to the tenant"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I have passed it on
        </button>
      }
    >
      <p className="modal-lead">
        {tenant.name} can now sign in with their email and this temporary password. They will be made to choose their own
        before they can do anything, so it stops working as soon as they use it.
      </p>

      <div className="handover">
        <span className="handover-label">Email</span>
        <strong>{tenant.email}</strong>
        <span className="handover-label">Temporary password</span>
        <code className="handover-code">{password}</code>
      </div>

      <div className="row-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={copy}>
          Copy the password
        </button>
        <a
          className="btn btn-ghost btn-sm"
          href={`sms:${tenant.phone}?body=${encodeURIComponent(
            `Your Cameroon Homes sign-in: ${tenant.email} with temporary password ${password}. Choose your own password when you sign in.`
          )}`}
        >
          Send it by SMS
        </a>
      </div>

      <p className="notice notice-warn">
        This is the only time it is shown. If it goes missing, issue a new one from the tenant list.
      </p>
    </Modal>
  );
};

const CreateTenantForm = ({ onCreated, onClose }) => {
  const { cities } = useMeta();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const result = await api.post('/landlord/tenants', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city || undefined
      });

      if (result.alreadyRegistered) {
        toast.info(`${result.tenant.name} already has an account, so you can put them straight onto a lease.`);
        onCreated(null);
      } else {
        onCreated(result);
      }
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add a tenant"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="tenant-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating...' : 'Create the account'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        For somebody already renting from you, or about to. You will be given a temporary password to pass on, and they
        choose their own when they first sign in.
      </p>

      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="tenant-form" className="stack" onSubmit={submit}>
        <Field label="Full name" error={errors.name} required>
          <input
            type="text"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            placeholder="Ngwa Emmanuel"
            required
          />
        </Field>

        <div className="grid-2">
          <Field label="Email" hint="They sign in with this." error={errors.email} required>
            <input type="email" value={form.email} onChange={(event) => set({ email: event.target.value })} required />
          </Field>
          <Field label="Phone number" error={errors.phone} required>
            <input
              type="tel"
              value={form.phone}
              onChange={(event) => set({ phone: event.target.value })}
              placeholder="+237 6 70 00 00 00"
              required
            />
          </Field>
        </div>

        <Field label="Town" error={errors.city}>
          <select value={form.city} onChange={(event) => set({ city: event.target.value })}>
            <option value="">Not set</option>
            {cities.map((city) => (
              <option key={city.name} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
        </Field>
      </form>
    </Modal>
  );
};

const TenantDirectory = () => {
  useTitle('My tenants');
  const toast = useToast();
  const { data, loading, error, reload } = useFetch('/landlord/tenants');

  const [creating, setCreating] = useState(false);
  const [handover, setHandover] = useState(null);

  const resetPassword = async (tenant) => {
    try {
      const result = await api.post(`/landlord/tenants/${tenant.id}/reset-password`);
      setHandover({ tenant, password: result.temporaryPassword });
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const items = data.items || [];

  const columns = [
    {
      key: 'name',
      header: 'Tenant',
      render: (tenant) => (
        <div className="cell-listing">
          <Avatar name={tenant.name} url={tenant.avatarUrl} size={40} />
          <div>
            <strong>{tenant.name}</strong>
            <span className="muted">
              {tenant.phone}
              {whatsappLink(tenant.phone) ? (
                <>
                  {' - '}
                  <a href={whatsappLink(tenant.phone, `Hello ${tenant.name.split(' ')[0]}, `)} target="_blank" rel="noreferrer" className="link-strong">
                    WhatsApp
                  </a>
                </>
              ) : null}
            </span>
            <span className="muted">{tenant.email}</span>
          </div>
        </div>
      )
    },
    {
      key: 'current',
      header: 'Renting',
      render: (tenant) =>
        tenant.current ? (
          <div className="cell-stack">
            <Link to={`/landlord/leases/${tenant.current.id}`}>
              {tenant.current.unitLabel ? `${tenant.current.unitLabel}, ` : ''}
              {tenant.current.propertyTitle}
            </Link>
            <span className="muted">
              {formatXaf(tenant.current.rent)} a {rentPeriodNoun(tenant.current.billingPeriod)} since{' '}
              {formatDate(tenant.current.startDate)}
            </span>
          </div>
        ) : (
          <span className="muted">No live lease</span>
        )
    },
    {
      key: 'state',
      header: 'Account',
      render: (tenant) => (
        <div className="cell-stack">
          {tenant.mustChangePassword ? (
            <StatusBadge status="pending" label="Has not signed in yet" />
          ) : (
            <StatusBadge status="approved" label="Active" />
          )}
          <span className="muted">
            {tenant.tenancyCount} lease{tenant.tenancyCount === 1 ? '' : 's'} with you
          </span>
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (tenant) => (
        <div className="cell-actions">
          <Link to={`/landlord/leases/new?tenantId=${tenant.id}`} className="btn btn-outline btn-sm">
            New lease
          </Link>
          <Link to={`/landlord/rent?tenantId=${tenant.id}`} className="btn btn-ghost btn-sm">
            Rent
          </Link>
          <button type="button" className="link-btn" onClick={() => resetPassword(tenant)}>
            New password
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My tenants</h1>
          <p className="muted">
            Everybody you have a lease with, plus anyone you have set up an account for. {items.length} in total.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          Add a tenant
        </button>
      </header>

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'No tenants yet',
          message:
            'Add the people already renting from you, then draw up their lease. Anyone who applies through the marketplace and is approved shows up here too.'
        }}
      />

      {creating ? (
        <CreateTenantForm
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            reload();
            if (result) setHandover({ tenant: result.tenant, password: result.temporaryPassword });
          }}
        />
      ) : null}

      {handover ? (
        <PasswordHandover
          tenant={handover.tenant}
          password={handover.password}
          onClose={() => {
            setHandover(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default TenantDirectory;
